import { IAwardsAPITypes } from "@/types/GetAwardsApiType";
import { rtkQuerieSetup } from "../services/rtkQuerieSetup";
import { IAllStoresLocationType } from "@/types/GetAllStoresLocationApiType";
import { IfaqsApiTypes } from "@/types/GetAllTreatmeantfaqsApi";
import { FRONTEND_URL } from "@/util/config";
import { ITreatmentPhasesApiTypes } from "@/types/GetAllTreatmentPhasesApiTypes";
import { IConsultationTreatmentType } from "@/types/GetConsultationTreatmentApiType";
import { IMiscellaneousDetailsTypes } from "@/types/GetMiscellaneousDetailsApiType";
import { IGlobalContactDetailsApiType } from "@/types/GetGlobalContactDetailsApiType";
import { ICityListApiResponse } from "@/types/GetAllStoreCityListApiType";
import { IAllparentChildTreatmeantsApi } from "@/types/GetAllParentChildTreatmentApiType";
import { IGetAllFaqsAPIresponse } from "@/types/GetAllFaqsAPIresponse";
import { IOffersApiResponse } from "@/types/GetOfferApiType";
import { ICelebrationApiResponse } from "@/types/GetCelebrationApiType";
import { IGetCDlushPageApiType } from "@/types/GetCDlushPageApiType";
import { IBestSellerApiType } from "@/types/IBestSellerApiType";
import { IGetJobsPostingListApiType } from "@/types/GetJobsPostingListApiType";

export const clientSideGetApis = rtkQuerieSetup.injectEndpoints({
    endpoints: builder => ({
        GetAllAwardsData: builder.query<IAwardsAPITypes,void>({
            query: () => `/awards`,
        }),

        GetAllStoreLocationApi: builder.query<IAllStoresLocationType, { limit?: number; pageNumber?: number; cityName?:string|null } | void>({
            query: (params) => {
                const limit = params?.limit ?? 10;
                const pageNumber = params?.pageNumber ?? 1;
                const cityName = params?.cityName ?? '';
                return `${FRONTEND_URL}/common-reusable/get-all-stores?city=${cityName}&limit=${limit}&pageNumber=${pageNumber}`;
            },
        }),

        GetAllTreatmeantFaqsApi: builder.query<IfaqsApiTypes, { slug: string }>({
            query: (params) => `${FRONTEND_URL}/treatmeant/faqs/${params.slug}`,
        }),

        GetAllTreatmeantPhasesApi: builder.query<ITreatmentPhasesApiTypes, { slug: string }>({
            query: (params) => `${FRONTEND_URL}/treatmeant/treatment-phases/${params.slug}`,
        }),

        GetConsultationTreatmeantDetailsApi: builder.query<IConsultationTreatmentType, { slug: string }>({
            query: (params) => `${FRONTEND_URL}/treatmeant/consultation/${params.slug}`,
        }),

        GetMiscellaneousDetailsAPI : builder.query<IMiscellaneousDetailsTypes, void>({
            query: () => `${FRONTEND_URL}/miscellaneous-settings`,
        }),

        GetGlobalContactDetailsAPI : builder.query<IGlobalContactDetailsApiType, void>({
            query: () => `${FRONTEND_URL}/global-contact-details`,
        }),

        GetAllStoreCityAPI : builder.query<ICityListApiResponse, void>({
            query: () => `${FRONTEND_URL}/common-reusable/get-all-unique-cities`,
        }),

        GetAllParentChildTreatmentAPI: builder.query<IAllparentChildTreatmeantsApi, {templateType:string}>({
            query: (params) => `${FRONTEND_URL}/treatmeant/all-parentChild-treatmeants/${params.templateType}`,
        }),

        GetAllFaqsAPI : builder.query<IGetAllFaqsAPIresponse, { limit?: number; pageNumber?: number; search?:string|null }>({
            query: (params) => {
                const limit = params?.limit ?? 10;
                const pageNumber = params?.pageNumber ?? 1;
                const search = params?.search ?? '';
                return `${FRONTEND_URL}/common-reusable/all-faqs?search=${search}&limit=${limit}&pageNumber=${pageNumber}`
            }
        }),

        /** Mirrors `getOfferServerAPI`: `slug` omitted from request when null/empty. */
        GetOfferDetailAPI: builder.query<IOffersApiResponse, { slug: string | null }>({
            query: ({ slug }) => {
                if (slug) {
                    return { url: `${FRONTEND_URL}/offers`, params: { slug } };
                }
                return `${FRONTEND_URL}/offers`;
            },
        }),

        /** Active celebration within date window (global, or treatment via slug). */
        GetCelebrationAPI: builder.query<ICelebrationApiResponse, { slug?: string | null } | void>({
            query: (arg) => {
                const slug = arg && typeof arg === "object" ? arg.slug : null;
                if (slug) {
                    return { url: `${FRONTEND_URL}/celebrations`, params: { slug } };
                }
                return `${FRONTEND_URL}/celebrations`;
            },
        }),

        PostScheduleCallBackAPI: builder.mutation<
            { success?: boolean; message?: string; data?: unknown },
            FormData
        >({
            query: (formData) => ({
                url: `${FRONTEND_URL}/scheduled-callbacks`,
                method: "POST",
                body: formData,
            }),
        }),

        GetCdLushDetailsAPI : builder.query<IGetCDlushPageApiType, void>({
            query: () => `${FRONTEND_URL}/cd-lush`,
        }),

        GetBestSellersDetailsAPI : builder.query<IBestSellerApiType, void>({
            query: () => `${FRONTEND_URL}/bestseller`,
        }),
        GetJobsPostingDetailsAPI: builder.query<
            IGetJobsPostingListApiType,
            { pageNumber?: number; limit?: number } | void
        >({
            query: (arg) => {
                const pageNumber = arg?.pageNumber ?? 1;
                const limit = arg?.limit ?? 10;
                return {
                    url: `${FRONTEND_URL}/career-jobs`,
                    params: { pageNumber, limit },
                };
            },
        }),
        ApplyToJobAPI: builder.mutation<
            { success?: boolean; message?: string; data?: unknown },
            FormData
        >({
            query: (formData) => ({
                url: `${FRONTEND_URL}/career-jobs/apply`,
                method: "POST",
                body: formData,
            }),
        }),
        SubscribeMembershipAPI: builder.mutation<
            { success?: boolean; message?: string; data?: unknown },
            { email: string }
        >({
            query: (body) => ({
                url: `${FRONTEND_URL}/subscription-members`,
                method: "POST",
                body,
            }),
        }),
    })
})


export const { useGetAllAwardsDataQuery, useGetAllStoreLocationApiQuery, useGetAllTreatmeantFaqsApiQuery, useGetAllTreatmeantPhasesApiQuery, useGetConsultationTreatmeantDetailsApiQuery, useGetMiscellaneousDetailsAPIQuery, useGetGlobalContactDetailsAPIQuery, useGetAllStoreCityAPIQuery, useGetAllParentChildTreatmentAPIQuery, useGetAllFaqsAPIQuery, useGetOfferDetailAPIQuery, useGetCelebrationAPIQuery, usePostScheduleCallBackAPIMutation, useGetCdLushDetailsAPIQuery, useGetBestSellersDetailsAPIQuery, useGetJobsPostingDetailsAPIQuery, useApplyToJobAPIMutation, useSubscribeMembershipAPIMutation} = clientSideGetApis;